import React from "react"

import { logger } from "@/lib/logger"
import type { AudioRegionParams } from "@/components/audio-player/AudioPlayer"
import { TierGauge } from "@/components/charts/TierGauge"
import type {
  MetricOutputDisplaySource,
  RunMetricOutput,
} from "@/types/RunMetricOutput"

import { NumericalMetricRecord } from "../base/NumericalMetricRecord"
import { getStat } from "../helpers/getStat"
import { WithAudioGateMixin } from "../mixins/WithAudioGateMixin"
import {
  GAUGE_MAX_LUFS,
  GAUGE_MIN_LUFS,
  LOUD_OUTLIER_FILL,
  loudnessTier,
  NORMAL_HIGH_LUFS,
  NORMAL_LOW_LUFS,
  OUTLIER_DEVIATION_LU,
  QUIET_OUTLIER_FILL,
  TARGET_LUFS,
  targetDeltaLabel,
} from "./lufsValues"

const log = logger("LufsMetric")

const OUTLIER_REGION_ALPHA = 0.25

interface DurationSummaryStat {
  mean?: number
}

// No unit suffix on the value badge: "LUFS" is long and inferable from the
// metric name. The deep dive is the one place that spells it out.
export class LufsMetricRecord extends WithAudioGateMixin(
  NumericalMetricRecord,
) {
  overviewSize = "small" as const

  // Summary "slider": headline LUFS value, tier badge, gauge with the shaded
  // normal band; one trailing line covers the band and the target distance.
  private renderSummary(lufs: number): React.ReactNode {
    return (
      <TierGauge
        value={lufs}
        tier={loudnessTier(lufs)}
        min={GAUGE_MIN_LUFS}
        max={GAUGE_MAX_LUFS}
        band={{ low: NORMAL_LOW_LUFS, high: NORMAL_HIGH_LUFS }}
        formatValue={(v) => v.toFixed(1)}
        unit="LUFS"
        bandNote={`Shaded = normal (${NORMAL_LOW_LUFS} to ${NORMAL_HIGH_LUFS} LUFS) · ${targetDeltaLabel(lufs, TARGET_LUFS)}.`}
      />
    )
  }

  // Volume outlier regions (backend emits one subvalue per merged region).
  private renderOutliersNote(mo: RunMetricOutput): React.ReactNode | null {
    const outlierCount = mo.subvalues_by_timestamp?.length ?? 0
    if (outlierCount === 0) {
      return null
    }
    return (
      <p className="text-xs text-coval-text-muted">
        {outlierCount} volume outlier{outlierCount === 1 ? "" : "s"} (≥
        {OUTLIER_DEVIATION_LU} LU from average) marked on the waveform — red =
        loud, blue = quiet.
      </p>
    )
  }

  // Scope context, present only on speaker-scoped instances (whole-clip reads
  // come from the cached observation and carry no stats).
  private renderScopeContext(mo: RunMetricOutput): React.ReactNode | null {
    const durationStat = getStat<DurationSummaryStat>(
      mo,
      "summary",
      "measured_audio_seconds",
    )
    if (durationStat?.mean == null) {
      return null
    }
    const segmentCount: unknown = mo.result?.count
    const segments =
      typeof segmentCount === "number"
        ? ` across ${segmentCount} speaking segment${segmentCount === 1 ? "" : "s"}`
        : ""
    return (
      <p className="text-xs text-coval-text-muted">
        Measured over {durationStat.mean.toFixed(1)} s of scoped speech
        {segments}. Short scopes are less reliable.
      </p>
    )
  }

  _renderDeepDive(mo: RunMetricOutput): React.ReactNode {
    try {
      const lufs = mo.float_value
      if (lufs == null) {
        return (
          <div className="text-center py-8 text-coval-text-muted">
            No loudness data available
          </div>
        )
      }
      return (
        <div className="space-y-2">
          {this.renderSummary(lufs)}
          {this.renderOutliersNote(mo)}
          {this.renderScopeContext(mo)}
        </div>
      )
    } catch (error) {
      log.error(
        "Error rendering LUFS deep dive",
        error instanceof Error ? error : undefined,
      )
      return <div className="text-red-500">Error displaying metric details</div>
    }
  }

  getAudioRegions(mo: MetricOutputDisplaySource): AudioRegionParams[] {
    if (mo?.error_message && mo?.error_message !== "") {
      return []
    }
    try {
      const integratedLufs = mo.float_value
      return (mo.subvalues_by_timestamp ?? []).flatMap((subvalue, index) => {
        if (
          subvalue.float_value == null ||
          subvalue.start_offset == null ||
          subvalue.end_offset == null
        ) {
          return []
        }
        const isLoud =
          integratedLufs == null || subvalue.float_value > integratedLufs
        return [
          {
            id: `lufs_${isLoud ? "loud" : "quiet"}:${index}`,
            start: subvalue.start_offset,
            end: subvalue.end_offset,
            color: `rgba(${isLoud ? LOUD_OUTLIER_FILL : QUIET_OUTLIER_FILL}, ${OUTLIER_REGION_ALPHA})`,
            drag: false,
            resize: false,
          },
        ]
      })
    } catch (error) {
      log.debug("LUFS outlier regions unavailable or could not parse", {
        error,
      })
      return []
    }
  }
}
